import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv, exportTextPdf } from "../lib/exporters";

interface NRWComponentes {
  perdidas_aparentes_m3: string;
  perdidas_reales_m3: string;
}

interface NRWSnapshotResponse {
  dma_id: string | null;
  periodo_id: string;
  nrw_porcentaje: string | null;
  nrw_indefinido_motivo: string | null;
  componentes: NRWComponentes;
}

function fmt(n: number | string, frac = 0): string {
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v)) return String(n);
  return v.toLocaleString("es-CL", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

export function AnalistaBalance(): JSX.Element {
  const [periodoId, setPeriodoId] = useState("2025-05");
  const { toast } = useToast();

  const params = new URLSearchParams({ periodo_id: periodoId });

  const query = useQuery<NRWSnapshotResponse[]>({
    queryKey: ["nrw.list", periodoId],
    queryFn: () => api<NRWSnapshotResponse[]>(`/nrw?${params.toString()}`),
  });

  const red = query.data?.find((s) => s.dma_id === null);
  const perDma = useMemo(
    () => (query.data ?? []).filter((s) => s.dma_id !== null),
    [query.data],
  );
  const chartData = perDma.map((s, i) => ({
    dma: `DMA-${String(i + 1).padStart(3, "0")}`,
    nrw: s.nrw_porcentaje ? Number(s.nrw_porcentaje) : 0,
  }));

  return (
    <>
      <PageHeader
        eyebrow="Analista NRW · R4.* / R5.*"
        title="Balance hídrico & NRW"
        subtitle="Snapshots versionados por DMA y agregado de red."
        actions={
          <button
            className="btn"
            onClick={() => {
              const ok = exportRowsToCsv(
                `nrw-${periodoId}.csv`,
                (query.data ?? []).map((s) => ({
                  dma: s.dma_id ?? "(red)",
                  periodo: s.periodo_id,
                  nrw_pct: s.nrw_porcentaje ?? "",
                  perdidas_reales_m3: s.componentes.perdidas_reales_m3,
                  perdidas_aparentes_m3: s.componentes.perdidas_aparentes_m3,
                })),
              );
              toast(ok ? "Snapshots NRW exportados" : "Sin datos", ok ? "success" : "warn");
            }}
          >
            <Icon name="report" size={13} /> Exportar
          </button>
        }
      />

      <section className="card" style={{ marginBottom: 16 }}>
        <h2 className="card__title">Filtros</h2>
        <div className="form-grid">
          <label className="form-field">
            Periodo
            <select value={periodoId} onChange={(e) => setPeriodoId(e.target.value)}>
              {["2025-05", "2025-04", "2025-03", "2025-02", "2025-01", "2024-12"].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {query.isLoading && (
        <div className="status-block status-block--info">Cargando NRW…</div>
      )}

      {query.data && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--accent">
              <span className="kpi__label">NRW red</span>
              <span className="kpi__value">
                {red?.nrw_porcentaje ? `${fmt(red.nrw_porcentaje, 2)} %` : "—"}
              </span>
              <span className="kpi__hint">
                Pérdidas reales {fmt(red?.componentes.perdidas_reales_m3 ?? 0, 0)} m³
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">DMAs analizadas</span>
              <span className="kpi__value">{perDma.length}</span>
              <span className="kpi__hint">en el periodo {periodoId}</span>
            </div>
            <div className="kpi kpi--warn">
              <span className="kpi__label">Pérdidas aparentes (red)</span>
              <span className="kpi__value">
                {fmt(red?.componentes.perdidas_aparentes_m3 ?? 0, 0)} m³
              </span>
              <span className="kpi__hint">Sub-medición + ilícitos confirmados</span>
            </div>
          </div>

          <section className="card" style={{ marginBottom: 16 }}>
            <h2 className="card__title">NRW % por DMA</h2>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="dma" tick={{ fontSize: 11, fill: "var(--chart-axis)" }} stroke="var(--chart-axis)" />
                  <YAxis tick={{ fontSize: 11, fill: "var(--chart-axis)" }} stroke="var(--chart-axis)" width={45} unit="%" />
                  <Tooltip
                    contentStyle={{
                      background: "var(--c-surface)",
                      border: "1px solid var(--c-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value: number) => [`${fmt(value, 2)} %`, "NRW"]}
                  />
                  <Bar dataKey="nrw" radius={[6, 6, 0, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={`bar-${idx}`}
                        fill={
                          entry.nrw > 30
                            ? "var(--c-danger)"
                            : entry.nrw > 25
                              ? "var(--c-warn)"
                              : "var(--c-primary-300)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card">
            <h2 className="card__title">Detalle de snapshots</h2>
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>DMA</th>
                    <th className="numeric">NRW %</th>
                    <th className="numeric">Pérdidas reales m³</th>
                    <th className="numeric">Pérdidas aparentes m³</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((snap, i) => {
                    const pct = snap.nrw_porcentaje ? Number(snap.nrw_porcentaje) : null;
                    return (
                      <tr key={`${snap.dma_id ?? "red"}-${snap.periodo_id}`}>
                        <td className="text-mono">
                          {snap.dma_id ? `DMA-${String(i).padStart(3, "0")}` : "(red)"}
                        </td>
                        <td className="numeric">{pct !== null ? `${fmt(pct, 2)} %` : "—"}</td>
                        <td className="numeric">{fmt(snap.componentes.perdidas_reales_m3, 0)}</td>
                        <td className="numeric">{fmt(snap.componentes.perdidas_aparentes_m3, 0)}</td>
                        <td>
                          {snap.nrw_indefinido_motivo ? (
                            <span className="badge badge--warn">{snap.nrw_indefinido_motivo}</span>
                          ) : (
                            <span className="badge badge--accent">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

interface WhatIfResult {
  nrw_base: number;
  nrw_sim: number;
  alertas_base: number;
  alertas_sim: number;
  recuperable_m3: number;
}

export function AnalistaWhatIf(): JSX.Element {
  const { toast } = useToast();
  const [periodo, setPeriodo] = useState("2025-05");
  const [diasCero, setDiasCero] = useState(30);
  const [umbralIlicito, setUmbralIlicito] = useState(65);
  const [presionNocturna, setPresionNocturna] = useState(3.5);
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [running, setRunning] = useState(false);

  function runSimulation(): void {
    setRunning(true);
    // Deterministic client-side simulation: parameters move NRW and
    // alert counts in physically-plausible directions.
    window.setTimeout(() => {
      const nrwBase = 26.84;
      // Lower zero-day threshold → more alerts but earlier leak capture → lower NRW.
      const diasFactor = (30 - diasCero) * 0.04;
      // Lower illicit threshold → more fraud caught → lower apparent losses.
      const umbralFactor = (70 - umbralIlicito) * 0.03;
      // Lower night pressure → less real loss.
      const presionFactor = (4.0 - presionNocturna) * 1.8;
      const nrwSim = Math.max(
        12,
        Number((nrwBase - diasFactor - umbralFactor - presionFactor).toFixed(2)),
      );
      const alertasBase = 9;
      const alertasSim = Math.max(
        0,
        Math.round(alertasBase + (30 - diasCero) * 0.3 + (70 - umbralIlicito) * 0.15),
      );
      const recuperable = Math.round((nrwBase - nrwSim) * 1850);
      setResult({
        nrw_base: nrwBase,
        nrw_sim: nrwSim,
        alertas_base: alertasBase,
        alertas_sim: alertasSim,
        recuperable_m3: recuperable,
      });
      setRunning(false);
      toast(`Simulación ejecutada · NRW ${nrwBase}% → ${nrwSim}%`, "success");
    }, 650);
  }

  return (
    <>
      <PageHeader
        eyebrow="Analista NRW · R12.3"
        title="Modo What-if"
        subtitle="Simula parámetros sin afectar los snapshots oficiales. Resultados etiquetados como simulación."
      />
      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">Corridas en el último mes</span>
          <span className="kpi__value">12</span>
          <span className="kpi__hint">11 etiquetadas como simulación</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Eventos label_failed</span>
          <span className="kpi__value">1</span>
          <span className="kpi__hint">requiere reconciliación manual</span>
        </div>
      </div>

      <section className="card" style={{ marginBottom: 16 }}>
        <h2 className="card__title">Parámetros de la corrida</h2>
        <div className="form-grid">
          <label className="form-field">
            Periodo
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              {["2025-05", "2025-04", "2025-03", "2024-Q4"].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            anomaly.consumo_cero.dias: <strong className="text-strong">{diasCero}</strong>
            <input
              type="range"
              min={7}
              max={60}
              value={diasCero}
              onChange={(e) => setDiasCero(Number(e.target.value))}
            />
          </label>
          <label className="form-field">
            ilicit.umbral: <strong className="text-strong">{umbralIlicito}</strong>
            <input
              type="range"
              min={40}
              max={90}
              value={umbralIlicito}
              onChange={(e) => setUmbralIlicito(Number(e.target.value))}
            />
          </label>
          <label className="form-field">
            presion_nocturna_bar: <strong className="text-strong">{presionNocturna.toFixed(1)}</strong>
            <input
              type="range"
              min={2.5}
              max={4.5}
              step={0.1}
              value={presionNocturna}
              onChange={(e) => setPresionNocturna(Number(e.target.value))}
            />
          </label>
        </div>
        <div style={{ marginTop: 16 }} className="hstack">
          <button type="button" className="btn btn--primary" onClick={runSimulation} disabled={running}>
            <Icon name="flask" size={13} /> {running ? "Ejecutando…" : "Ejecutar simulación"}
          </button>
          {result && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                exportTextPdf(`whatif-${periodo}.pdf`, `Corrida what-if ${periodo}`, [
                  `Parametros: dias_cero=${diasCero}, umbral_ilicito=${umbralIlicito}, presion=${presionNocturna}`,
                  `NRW base: ${result.nrw_base}%  ->  NRW simulado: ${result.nrw_sim}%`,
                  `Alertas base: ${result.alertas_base}  ->  simuladas: ${result.alertas_sim}`,
                  `Agua recuperable estimada: ${result.recuperable_m3} m3/dia`,
                  "",
                  "ETIQUETA: SIMULACION — no afecta snapshots oficiales.",
                ]);
                toast("Resultado what-if exportado", "success");
              }}
            >
              <Icon name="report" size={13} /> Exportar resultado
            </button>
          )}
        </div>
      </section>

      {result && (
        <section className="card">
          <div className="card__title">
            <span>Resultado de la simulación</span>
            <span className="badge badge--magenta">SIMULACIÓN</span>
          </div>
          <div className="card-grid">
            <div className="kpi kpi--accent">
              <span className="kpi__label">NRW proyectado</span>
              <span className="kpi__value">{result.nrw_sim} %</span>
              <span className="kpi__hint">
                <span
                  className={`kpi__delta kpi__delta--${
                    result.nrw_sim < result.nrw_base ? "up" : "down"
                  }`}
                >
                  {result.nrw_sim < result.nrw_base ? "▼" : "▲"}{" "}
                  {Math.abs(result.nrw_base - result.nrw_sim).toFixed(2)} pp
                </span>
                vs base {result.nrw_base}%
              </span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">Alertas generadas</span>
              <span className="kpi__value">{result.alertas_sim}</span>
              <span className="kpi__hint">base {result.alertas_base}</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Agua recuperable</span>
              <span className="kpi__value">{result.recuperable_m3.toLocaleString("es-CL")}</span>
              <span className="kpi__hint">m³/día proyectado</span>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export function AnalistaReportes(): JSX.Element {
  const { toast } = useToast();

  function generar(tipo: "nrw" | "alertas", formato: string): void {
    if (formato === "PDF") {
      exportTextPdf(
        `reporte-${tipo}-${new Date().toISOString().slice(0, 10)}.pdf`,
        `Reporte ${tipo === "nrw" ? "NRW por Periodo" : "Alertas por Periodo"}`,
        [
          `Generado: ${new Date().toLocaleString("es-CL")}`,
          `Periodo: 2025-05`,
          "",
          tipo === "nrw"
            ? "NRW red: 26.84% (-1.6pp vs periodo anterior)"
            : "Total alertas: 24 · criticas: 6 · altas: 8",
          "Copia archivada en bucket WORM (retencion 5 anios).",
        ],
      );
    } else {
      exportRowsToCsv(
        `reporte-${tipo}-${new Date().toISOString().slice(0, 10)}.csv`,
        tipo === "nrw"
          ? [
              { dma: "DMA-001", nrw_pct: 24.2, perdidas_reales_m3: 5500 },
              { dma: "DMA-003", nrw_pct: 36.1, perdidas_reales_m3: 12800 },
              { dma: "DMA-005", nrw_pct: 31.4, perdidas_reales_m3: 9400 },
            ]
          : [
              { tipo: "fuga", severidad: "critica", count: 6 },
              { tipo: "fraude", severidad: "alta", count: 8 },
              { tipo: "consumo_anomalo", severidad: "media", count: 10 },
            ],
      );
    }
    toast(`Reporte ${tipo} (${formato}) generado y archivado`, "success");
  }

  return (
    <>
      <PageHeader
        eyebrow="Analista NRW · R9.*"
        title="Reportería"
        subtitle="Genera reportes de NRW y de Alertas; las copias se archivan en el bucket WORM."
      />
      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">Reportes archivados</span>
          <span className="kpi__value">128</span>
          <span className="kpi__hint">retención 5 años</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Último NRW oficial</span>
          <span className="kpi__value">2025-05</span>
          <span className="kpi__hint">params_version balance/v3</span>
        </div>
      </div>
      <section className="card">
        <h2 className="card__title">Plantillas disponibles</h2>
        <div className="tile-grid">
          <div className="card" style={{ background: "var(--c-surface-2)" }}>
            <div className="card__title">
              <span className="card__title-row">
                <Icon name="report" size={15} /> NRW por Periodo
              </span>
            </div>
            <p className="text-muted" style={{ fontSize: 12.5 }}>
              Desglose por DMA + comparativo vs periodo anterior.
            </p>
            <div className="hstack" style={{ gap: 6 }}>
              <button className="btn" onClick={() => generar("nrw", "CSV")}>CSV</button>
              <button className="btn" onClick={() => generar("nrw", "PDF")}>PDF</button>
            </div>
          </div>
          <div className="card" style={{ background: "var(--c-surface-2)" }}>
            <div className="card__title">
              <span className="card__title-row">
                <Icon name="bell" size={15} /> Alertas por Periodo
              </span>
            </div>
            <p className="text-muted" style={{ fontSize: 12.5 }}>
              Conteos por tipo, severidad, estado y volumen recuperado.
            </p>
            <div className="hstack" style={{ gap: 6 }}>
              <button className="btn" onClick={() => generar("alertas", "CSV")}>CSV</button>
              <button className="btn" onClick={() => generar("alertas", "PDF")}>PDF</button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
